import { useState, useEffect } from 'react';
import {
  Rocket,
  CheckCircle2,
  XCircle,
  Clock,
  X,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  ExternalLink,
  Lock,
  RotateCw,
  Ban,
  SkipForward,
  Minimize2,
  Slash,
} from 'lucide-react';
import { useDeployAll } from '../contexts/DeployAllContext';
import {
  RepoState,
  isTerminal,
  isInFlight,
  summarize,
  durationText,
} from '../utils/deployAllOrchestration';
import { RubyLoader } from './RubyLoader';
import { RubyGemMark } from './RubyGemMark';

/**
 * DeployAllPanel — the full-screen progress modal. Pure view over
 * DeployAllContext: all orchestration (dispatch, poll, phase advancement,
 * persistence) lives in the provider, so closing this modal only minimizes it —
 * the batch keeps running and can be reopened from the floating widget.
 */
export function DeployAllPanel() {
  const {
    batch, isModalOpen, loadingPlan,
    startBatch, cancelOne, skipOne, rerunOne,
    retryFailed, skipAndContinue, restart, minimize, discard,
  } = useDeployAll();

  const [confirmText, setConfirmText] = useState('');
  const [expandedPhases, setExpandedPhases] = useState<Record<number, boolean>>({ 1: true, 2: true });

  // Local 1s tick so elapsed timers advance without re-rendering other consumers.
  const active = isModalOpen && (batch?.status === 'running' || (batch ? Object.values(batch.progress).some(p => p.state === 'running' || p.state === 'queued' || p.state === 'dispatching') : false));
  const [, setElapsedTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setElapsedTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);

  if (!batch || !isModalOpen) return null;

  const { environment, product, status, currentPhaseIdx, plan, progress, batchStartedAt } = batch;
  const phases = plan?.phases || [];
  const summary = summarize(progress);
  const batchElapsed = batchStartedAt ? durationText(batchStartedAt) : '';
  const isTerminalStatus = status === 'done' || status === 'failed' || status === 'aborted';

  const togglePhase = (n: number) => setExpandedPhases(prev => ({ ...prev, [n]: !prev[n] }));

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-dark-900 border border-dark-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-dark-700 bg-dark-800">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${environment === 'prod' ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
              <Rocket size={20} className={environment === 'prod' ? 'text-red-400' : 'text-emerald-400'} />
            </div>
            <div>
              <h2 className="text-white text-lg font-semibold">
                Deploy All to {environment === 'prod' ? 'Production' : 'Dev'}
              </h2>
              <p className="text-xs text-gray-400">
                {product} · branch {environment === 'prod' ? 'main' : 'development'}
                {batchStartedAt && ` · elapsed ${batchElapsed}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {status !== 'preview' && (
              <button
                onClick={minimize}
                className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-dark-700"
                title="Minimize (keeps running)"
              >
                <Minimize2 size={18} />
              </button>
            )}
            <button
              onClick={status === 'preview' ? discard : minimize}
              className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-dark-700"
              title={status === 'preview' ? 'Close' : 'Minimize (keeps running)'}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingPlan && !plan && (
            <RubyLoader variant="section" label="Loading deployment plan…" className="py-12" />
          )}

          {plan && status === 'preview' && (
            <>
              <div className="mb-4 text-sm text-gray-300">
                <p>
                  This will deploy <span className="text-white font-semibold">{plan.totalRepos}</span> repos in{' '}
                  <span className="text-white font-semibold">{plan.phases.length}</span> phases.
                  Next phase only starts after the current one succeeds.
                </p>
                {plan.skippedRepos.length > 0 && (
                  <p className="mt-2 text-xs text-gray-500">
                    {plan.skippedRepos.length} non-deployable repos skipped: docs, scripts, assets, data-migration.
                  </p>
                )}
              </div>

              {plan.phases.map(ph => (
                <div key={ph.phase} className="mb-3 border border-dark-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => togglePhase(ph.phase)}
                    className="w-full flex items-center justify-between p-3 bg-dark-800 hover:bg-dark-750"
                  >
                    <div className="flex items-center gap-2">
                      {expandedPhases[ph.phase] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                      <span className="text-white font-semibold">Phase {ph.phase}: {ph.name}</span>
                      <span className="text-xs text-gray-500">({ph.repos.length} repos)</span>
                    </div>
                  </button>
                  {expandedPhases[ph.phase] && (
                    <div className="p-3 bg-dark-850">
                      <p className="text-xs text-gray-400 mb-2">{ph.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {ph.repos.map(r => (
                          <span key={r.repoName} className={`text-xs px-2 py-1 rounded ${r.deployLocked ? 'bg-yellow-500/20 text-yellow-300' : 'bg-dark-700 text-gray-300'}`}>
                            {r.deployLocked && <Lock size={10} className="inline mr-1" />}
                            {r.displayName || r.repoName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-6 pt-6 border-t border-dark-700">
                <label className="block text-sm text-gray-300 mb-2">
                  Type <span className="font-mono text-emerald-400">DEPLOY</span> to confirm:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  className="w-full bg-dark-800 border border-dark-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500"
                  placeholder="DEPLOY"
                />
              </div>
            </>
          )}

          {/* Running / Done / Failed / Aborted view — phased progress */}
          {plan && status !== 'preview' && (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-6 gap-2 mb-4 text-xs">
                <SummaryTile n={summary.success} label="success" cls="emerald" />
                <SummaryTile n={summary.failure} label="failed" cls="red" />
                <SummaryTile n={summary.cancelled} label="cancelled" cls="orange" />
                <SummaryTile n={summary.running} label="running" cls="blue" />
                <SummaryTile n={summary.pending} label="pending" cls="gray" />
                <SummaryTile n={summary.skipped} label="skipped" cls="yellow" />
              </div>

              {/* Phases */}
              {plan.phases.map((ph, idx) => {
                const isCurrent = idx === currentPhaseIdx;
                const isPast = idx < currentPhaseIdx;
                const isFuture = idx > currentPhaseIdx;
                const phStates = ph.repos.map(r => progress[r.repoName]?.state || 'pending');
                const phDone = phStates.every(isTerminal);
                const phFailed = phStates.some(s => s === 'failure' || s === 'cancelled');
                const phRunning = isCurrent && !phDone;
                const statusColor = phDone && !phFailed ? 'border-emerald-500/50 bg-emerald-500/5'
                  : phFailed ? 'border-red-500/50 bg-red-500/5'
                  : phRunning ? 'border-blue-500/50 bg-blue-500/5'
                  : 'border-dark-700 bg-dark-800';

                return (
                  <div key={ph.phase} className={`mb-3 border rounded-lg overflow-hidden ${statusColor}`}>
                    <button
                      onClick={() => togglePhase(ph.phase)}
                      className="w-full flex items-center justify-between p-3 hover:bg-dark-750/50"
                    >
                      <div className="flex items-center gap-2">
                        {expandedPhases[ph.phase] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
                        {phDone && !phFailed && <CheckCircle2 size={18} className="text-emerald-400" />}
                        {phFailed && <XCircle size={18} className="text-red-400" />}
                        {phRunning && <RubyGemMark size={18} drawing />}
                        {isFuture && <Clock size={18} className="text-gray-500" />}
                        <span className="text-white font-semibold">Phase {ph.phase}: {ph.name}</span>
                        <span className="text-xs text-gray-500">
                          {phStates.filter(s => s === 'success').length}/{phStates.length} done
                        </span>
                      </div>
                      {isPast && phDone && !phFailed && <span className="text-xs text-emerald-400">✓ complete</span>}
                      {isCurrent && phRunning && <span className="text-xs text-blue-400">running…</span>}
                      {isFuture && <span className="text-xs text-gray-500">queued</span>}
                    </button>
                    {expandedPhases[ph.phase] && (
                      <div className="p-3 bg-dark-850/50 space-y-1">
                        {ph.repos.map(r => {
                          const p = progress[r.repoName] || { state: 'pending' as RepoState };
                          const pendingLabel = isCurrent ? 'queued' : 'waiting';
                          return (
                            <div key={r.repoName} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-dark-800/50 group">
                              <div className="flex items-center gap-2 min-w-0">
                                <RepoStateIcon state={p.state} />
                                <span className="text-gray-200 truncate">{r.displayName || r.repoName}</span>
                                {p.state === 'pending' && (
                                  <span className="text-[10px] uppercase tracking-wider text-gray-500">{pendingLabel}</span>
                                )}
                                {p.message && <span className="text-xs text-gray-500 ml-1 truncate">· {p.message}</span>}
                              </div>
                              <div className="flex items-center gap-1.5 text-xs text-gray-500 shrink-0">
                                {p.startedAt && p.state !== 'pending' && (
                                  <span>{durationText(p.startedAt, p.finishedAt)}</span>
                                )}
                                {p.runUrl && (
                                  <a href={p.runUrl} target="_blank" rel="noreferrer" className="hover:text-white p-1" title="View run on GitHub">
                                    <ExternalLink size={12} />
                                  </a>
                                )}
                                {/* Per-repo controls */}
                                {isInFlight(p.state) && (
                                  <button
                                    onClick={() => cancelOne(r.repoName)}
                                    className="text-orange-400 hover:text-orange-300 p-1 rounded hover:bg-orange-500/10"
                                    title="Stop — cancel this GitHub run"
                                  >
                                    <Ban size={13} />
                                  </button>
                                )}
                                {!isTerminal(p.state) && p.state !== 'pending' && (
                                  <button
                                    onClick={() => skipOne(r.repoName)}
                                    className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-yellow-500/10"
                                    title="Skip — stop blocking the phase"
                                  >
                                    <SkipForward size={13} />
                                  </button>
                                )}
                                {p.state === 'pending' && !isFuture && (
                                  <button
                                    onClick={() => skipOne(r.repoName)}
                                    className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-yellow-500/10"
                                    title="Skip this repo"
                                  >
                                    <SkipForward size={13} />
                                  </button>
                                )}
                                {isTerminal(p.state) && (
                                  <button
                                    onClick={() => rerunOne(r.repoName)}
                                    className="text-blue-400 hover:text-blue-300 p-1 rounded hover:bg-blue-500/10"
                                    title="Rerun this repo"
                                  >
                                    <RotateCw size={13} />
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {status === 'done' && (
                <div className="mt-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 text-center">
                  <CheckCircle2 className="inline text-emerald-400 mr-2" size={20} />
                  <span className="text-emerald-300 font-semibold">Batch complete</span>
                  <p className="text-xs text-emerald-400/70 mt-1">
                    {summary.success} success · {summary.skipped} skipped
                    {summary.cancelled > 0 && ` · ${summary.cancelled} cancelled`} · {batchElapsed} elapsed
                  </p>
                </div>
              )}
              {status === 'failed' && (
                <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="text-red-400" size={20} />
                    <span className="text-red-300 font-semibold">Phase {phases[currentPhaseIdx]?.phase} halted</span>
                  </div>
                  <p className="text-xs text-red-400/70 mb-3">
                    {summary.failure} failed{summary.cancelled > 0 && `, ${summary.cancelled} cancelled`}. Later phases are blocked until recovered — retry, skip, or use the per-repo controls above.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={retryFailed} className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-4 py-2 rounded-lg">
                      Retry Failed
                    </button>
                    <button onClick={skipAndContinue} className="bg-yellow-600 hover:bg-yellow-700 text-white text-sm px-4 py-2 rounded-lg">
                      Skip &amp; Continue
                    </button>
                  </div>
                </div>
              )}
              {status === 'aborted' && (
                <div className="mt-4 bg-gray-500/10 border border-gray-500/30 rounded-lg p-4 text-center">
                  <span className="text-gray-300 font-semibold">Batch aborted</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-dark-700 bg-dark-800">
          {status === 'preview' && (
            <>
              <button onClick={discard} className="text-gray-300 hover:text-white px-4 py-2 rounded-lg">
                Cancel
              </button>
              <button
                onClick={() => startBatch()}
                disabled={confirmText !== 'DEPLOY' || !plan || loadingPlan}
                className={`px-4 py-2 rounded-lg text-white font-medium ${
                  confirmText === 'DEPLOY' && plan && !loadingPlan
                    ? environment === 'prod' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-dark-700 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Rocket size={16} className="inline mr-2" />
                Deploy All
              </button>
            </>
          )}
          {status !== 'preview' && (
            <>
              {isTerminalStatus && (
                <>
                  <button onClick={discard} className="text-gray-300 hover:text-white px-4 py-2 rounded-lg flex items-center gap-1.5">
                    <Slash size={14} /> Discard
                  </button>
                  <button onClick={() => restart()} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg flex items-center gap-1.5">
                    <RotateCw size={14} /> Run Again
                  </button>
                </>
              )}
              <button onClick={minimize} className="bg-dark-700 hover:bg-dark-600 text-white px-4 py-2 rounded-lg flex items-center gap-1.5">
                <Minimize2 size={14} /> Minimize {status === 'running' || summary.running > 0 ? '(keeps running)' : ''}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const TILE_CLS: Record<string, string> = {
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  red: 'bg-red-500/10 border-red-500/30 text-red-400',
  orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
  gray: 'bg-gray-500/10 border-gray-500/30 text-gray-400',
  yellow: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400',
};

function SummaryTile({ n, label, cls }: { n: number; label: string; cls: string }) {
  return (
    <div className={`border rounded-lg p-2 text-center ${TILE_CLS[cls]}`}>
      <div className="font-semibold text-lg leading-none">{n}</div>
      <div className="opacity-70 mt-1">{label}</div>
    </div>
  );
}

function RepoStateIcon({ state }: { state: RepoState }) {
  switch (state) {
    case 'success': return <CheckCircle2 size={14} className="text-emerald-400 shrink-0" />;
    case 'failure': return <XCircle size={14} className="text-red-400 shrink-0" />;
    case 'cancelled': return <Ban size={14} className="text-orange-400 shrink-0" />;
    case 'skipped': return <AlertTriangle size={14} className="text-yellow-400 shrink-0" />;
    case 'running':
    case 'dispatching':
    case 'queued': return <span className="shrink-0 inline-flex"><RubyGemMark size={14} drawing /></span>;
    default: return <Clock size={14} className="text-gray-500 shrink-0" />;
  }
}
